import { useEffect, useMemo, useRef, useState } from 'react'
import { api, imageSrc } from '../services/api'
import {
  CATEGORIES,
  DEVICES,
  PRODUCTS,
  REVIEWS,
  OFFERS,
} from '../data/site'

// Lowest display order first
export const byOrder = (a, b) =>
  (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)

// Public site data loaded from the API
function useList(name, fallback, map) {
  const [data, setData] = useState(fallback)
  // keep the latest map function in a ref, so the request below only depends on `name`
  // (a new [] or a new function on every render used to restart the request forever)
  const mapRef = useRef(map)
  mapRef.current = map

  useEffect(() => {
    let live = true

    api.list(name)
      .then((response) => {
        if (!live) return

        if (!Array.isArray(response)) {
          console.error(
            `[iPOINT] Expected an array from /${name}, received:`,
            response
          )
          return
        }

        const shown = response
          .filter(
            (item) =>
              item.is_active == null ||
              Number(item.is_active) === 1
          )
          .sort(byOrder)
          .map((item) => mapRef.current(item))

        setData(shown)
      })
      .catch((error) => {
        if (live) {
          console.error(`[iPOINT] Failed to load /${name}:`, error)
        }
      })

    return () => {
      live = false
    }
  }, [name])

  return data
}

// Category fallback
const catFallback = CATEGORIES.map((name, i) => ({
  id: `f${i}`,
  name,
  image_url: '',
  is_active: 1,
}))

const mapCat = (category) => ({
  ...category,
  image_url: imageSrc(category.image_url),
})

// Get all product images, with image_url as fallback
const productImages = (product) => {
  let images = product.images

  if (typeof images === 'string') {
    try {
      images = JSON.parse(images)
    } catch {
      images = null
    }
  }

  if (!Array.isArray(images) || !images.length) {
    images = product.image_url ? [product.image_url] : []
  }

  return images.filter(Boolean).map((url) => imageSrc(url))
}

// Convert the API product to the format used by the frontend
const mapProduct = (product) => {
  const images = productImages(product)
  const sale = Number(product.sale_price)

  return {
    ...product,
    price: Number(product.price),
    salePrice: sale > 0 ? sale : null,
    isNew: !!Number(product.is_new),
    best: !!Number(product.is_best),
    image: images[0] || '',
    images,
    brand: product.brand || undefined,
    models: product.models
      ? String(product.models)
          .split(',')
          .map((model) => model.trim())
          .filter(Boolean)
      : undefined,
  }
}

const mapSlide = (slide) => ({
  id: slide.id,
  title: slide.title,
  text: slide.text,
  cta: slide.cta_text,
  to: slide.link || '/shop',
  image: imageSrc(slide.image_url),
  bg: '#0b1220',
})

const mapOffer = (offer) => offer.text

const mapReview = (review) => ({
  name: review.name,
  text: review.text,
  stars: Number(review.stars) || 5,
})

// Public API hooks
export const useCategories = () =>
  useList('categories', catFallback, mapCat)

export const useProducts = () =>
  useList('products', PRODUCTS, mapProduct)

export const useSlides = (fallback) =>
  useList('slides', fallback, mapSlide)

export const useOffers = () =>
  useList('offers', OFFERS, mapOffer)

export const useReviews = () =>
  useList('reviews', REVIEWS, mapReview)

// Phone brands added through the admin panel
const mapBrand = (brand) => ({
  ...brand,
  name: String(brand.name || '').trim(),
  models: brand.models
    ? String(brand.models)
        .split(',')
        .map((model) => model.trim())
        .filter(Boolean)
    : [],
})

export const useBrands = () =>
  useList('brands', [], mapBrand)

// Combine default devices and admin-managed brands
export const mergeDevices = (brands = []) => {
  const map = Object.fromEntries(
    Object.entries(DEVICES).map(([brand, models]) => [
      brand,
      [...models],
    ])
  )

  brands.forEach((brand) => {
    const name = String(brand.name || '').trim()
    if (!name) return

    const models = Array.isArray(brand.models)
      ? brand.models
      : String(brand.models || '')
          .split(',')
          .map((model) => model.trim())
          .filter(Boolean)

    map[name] = models.length ? models : map[name] || []
  })

  return map
}

export function useBrandMap() {
  const brands = useBrands()

  return useMemo(
    () => mergeDevices(brands),
    [brands]
  )
}

// Find the brand associated with a phone model
const brandOf = (model, map = DEVICES) =>
  Object.keys(map).find((brand) => map[brand].includes(model))

// Build brands and models from products
export function buildDevices(products, map = DEVICES) {
  const found = {}

  const add = (brand, model) => {
    if (!found[brand]) found[brand] = new Set()
    if (model) found[brand].add(model)
  }

  products.forEach((product) => {
    if (product.brand) {
      add(product.brand)
      ;(product.models || []).forEach((model) =>
        add(product.brand, model)
      )
    } else {
      ;(product.models || []).forEach((model) =>
        add(brandOf(model, map) || 'Other', model)
      )
    }
  })

  const known = Object.keys(map)
  const brandRank = (brand) =>
    known.includes(brand) ? known.indexOf(brand) : 1e6

  const modelRank = (brand, model) => {
    const index = (map[brand] || []).indexOf(model)
    return index < 0 ? 1e6 : index
  }

  return Object.fromEntries(
    Object.keys(found)
      .sort(
        (a, b) =>
          brandRank(a) - brandRank(b) ||
          a.localeCompare(b)
      )
      .map((brand) => [
        brand,
        [...found[brand]].sort(
          (a, b) =>
            modelRank(brand, a) - modelRank(brand, b) ||
            a.localeCompare(b)
        ),
      ])
  )
}

export function useDevices() {
  const products = useProducts()
  const map = useBrandMap()

  return useMemo(
    () => buildDevices(products, map),
    [products, map]
  )
}

// Build category -> brands mapping from products
export function buildCategoryBrands(products, map = DEVICES) {
  const known = Object.keys(map)
  const rank = (brand) =>
    known.includes(brand) ? known.indexOf(brand) : 1e6

  const found = {}

  products.forEach((product) => {
    if (!product.category) return

    const brands = product.brand
      ? [product.brand]
      : [
          ...new Set(
            (product.models || [])
              .map((model) => brandOf(model, map))
              .filter(Boolean)
          ),
        ]

    if (!found[product.category]) {
      found[product.category] = new Set()
    }

    brands.forEach((brand) =>
      found[product.category].add(brand)
    )
  })

  return Object.fromEntries(
    Object.entries(found).map(([category, brands]) => [
      category,
      [...brands].sort(
        (a, b) => rank(a) - rank(b) || a.localeCompare(b)
      ),
    ])
  )
}

export function useCategoryBrands() {
  const products = useProducts()
  const map = useBrandMap()

  return useMemo(
    () => buildCategoryBrands(products, map),
    [products, map]
  )
}